import { useState, useEffect, useCallback } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Copy, ChevronRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * CopyToModal — lets the user pick one or more projects (and an optional section
 * when exactly one project is selected) to copy tasks into.
 *
 * Props:
 *   taskIds   — array of task IDs to copy (single or bulk)
 *   onClose   — called when the modal should close
 *   onDone    — called after copies are created, receives count
 */
export function CopyToModal({ taskIds = [], onClose, onDone }) {
  const { projects, sections, copyTasksTo, activeProjectId } = useAppStore()

  const [selectedProjectIds, setSelectedProjectIds] = useState(() => new Set())
  const [selectedSectionId, setSelectedSectionId] = useState(null)

  // Pre-select the current project
  useEffect(() => {
    if (activeProjectId) setSelectedProjectIds(new Set([activeProjectId]))
  }, [activeProjectId])

  const availableProjects = projects.filter((p) => !p.isWorkspace)

  // Section picker only when exactly one project is selected
  const singleProject = selectedProjectIds.size === 1 ? [...selectedProjectIds][0] : null
  const projectSections = singleProject
    ? sections.filter((s) => s.projectId === singleProject).sort((a, b) => a.order - b.order)
    : []

  function toggleProject(id) {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
    // Clear section selection when toggling projects
    setSelectedSectionId(null)
  }

  const totalCopies = taskIds.length * selectedProjectIds.size

  const handleCopy = useCallback(() => {
    if (!selectedProjectIds.size) return
    for (const projectId of selectedProjectIds) {
      // Only use sectionId when a single project is selected (section picker is shown)
      const sectionId = singleProject === projectId ? selectedSectionId : null
      copyTasksTo(taskIds, projectId, sectionId)
    }
    onDone?.(totalCopies)
    onClose()
  }, [taskIds, selectedProjectIds, selectedSectionId, singleProject, copyTasksTo, totalCopies, onDone, onClose])

  const taskLabel = taskIds.length === 1 ? 'task' : `${taskIds.length} tasks`

  return (
    <Modal
      open
      onClose={onClose}
      title={`Copy ${taskLabel} to…`}
      size="sm"
    >
      <div className="flex flex-col gap-4">
        {/* Project picker — multi-select */}
        <div>
          <p className="text-xs text-text-muted mb-2">
            Projects
            {selectedProjectIds.size > 1 && (
              <span className="ml-1.5 text-text-faint">· {selectedProjectIds.size} selected</span>
            )}
          </p>
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto pr-1">
            {availableProjects.map((p) => {
              const selected = selectedProjectIds.has(p.id)
              return (
                <button
                  key={p.id}
                  onClick={() => toggleProject(p.id)}
                  className={cn(
                    'flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm text-left transition-colors',
                    selected
                      ? 'bg-accent/10 text-text'
                      : 'hover:bg-surface-raised text-text-muted hover:text-text'
                  )}
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: p.color || '#e63946' }}
                  />
                  <span className="flex-1 truncate font-medium">{p.name}</span>
                  {selected && singleProject === p.id && projectSections.length > 0 && (
                    <ChevronRight size={12} className="text-text-faint" />
                  )}
                  {selected && selectedProjectIds.size > 1 && (
                    <Check size={12} className="text-accent" />
                  )}
                </button>
              )
            })}
          </div>
        </div>

        {/* Section picker — only when exactly one project selected */}
        {singleProject && projectSections.length > 0 && (
          <div>
            <p className="text-xs text-text-muted mb-2">Section <span className="text-text-faint">(optional)</span></p>
            <div className="flex flex-col gap-1 max-h-36 overflow-y-auto pr-1">
              <button
                onClick={() => setSelectedSectionId(null)}
                className={cn(
                  'flex items-center px-3 py-2 rounded-xl text-sm text-left transition-colors',
                  selectedSectionId === null
                    ? 'bg-accent/10 text-text'
                    : 'hover:bg-surface-raised text-text-muted hover:text-text'
                )}
              >
                <span className="text-text-faint mr-2">—</span> No section
              </button>
              {projectSections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSelectedSectionId(s.id)}
                  className={cn(
                    'px-3 py-2 rounded-xl text-sm text-left transition-colors',
                    selectedSectionId === s.id
                      ? 'bg-accent/10 text-text'
                      : 'hover:bg-surface-raised text-text-muted hover:text-text'
                  )}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            variant="primary"
            onClick={handleCopy}
            disabled={!selectedProjectIds.size}
            className="flex-1 gap-1.5"
          >
            <Copy size={13} />
            {totalCopies === 1 ? 'Copy task' : `Copy ${totalCopies} tasks`}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
